import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  /**
   * Returns the current date.
   *
   * @returns Date - The current date.
   */
  public now(): Date {
    return new Date();
  }

  /**
   * Returns the current year.
   *
   * @returns number - The current year.
   */
  public currentYear(): number {
    return this.now().getFullYear();
  }

  /**
   * Checks if the given crop year matches the current year.
   *
   * @param cropYear - The crop year to compare.
   * @returns boolean - True if the crop year is the current year.
   */
  public isCurrentYear(cropYear: number): boolean {
    return cropYear === this.currentYear();
  }

  /**
   * Checks if the given date is today.
   *
   * @param date - The date to check.
   * @returns boolean - True if the date is today, false otherwise.
   */
  public isDateToday(date: Date): boolean {
    return this.isDateSameDay(date, this.now());
  }

  /**
   * Checks if the given date is in the past.
   *
   * @param date - The date to check.
   * @returns boolean - True if the date is in the past, false otherwise.
   */
  public isInThePast(date: Date): boolean {
    return date < this.now();
  }

  /**
   * Checks if the given date is in the future.
   *
   * @param date - The date to check.
   * @returns boolean - True if the date is in the future, false otherwise.
   */
  public isInTheFuture(date: Date): boolean {
    return date > this.now();
  }

  /**
   * Returns the day after the given date.
   *
   * @param date - The date to get the day after.
   * @returns Date - The day after the given date.
   */
  public dayAfter = (date: Date): Date => this.shiftDateByDays(date, 1);

  /**
   * Returns the day before the given date.
   *
   * @param date - The date to get the day before.
   * @returns Date - The day before the given date.
   */
  public dayBefore = (date: Date): Date => this.shiftDateByDays(date, -1);

  /**
   * Returns the date in the future by the given amount of days.
   *
   * @param amountOfDays - The amount of days to move the date.
   * @returns Date - The date in the future by the given amount of days.
   */
  public daysInTheFuture = (amountOfDays: number, cropYear?: number): Date =>
    this.shiftDateByDays(this.dateInCropYear(cropYear), amountOfDays);

  /**
   * Returns the date in the past by the given amount of days.
   *
   * @param amountOfDays - The amount of days to move the date.
   * @returns Date - The date in the past by the given amount of days.
   */
  public daysInThePast = (amountOfDays: number, cropYear?: number): Date =>
    this.shiftDateByDays(this.dateInCropYear(cropYear), -amountOfDays);

  /**
   * Checks if the first date is later than or equals to the second date.
   *
   * @param date1 - The first date.
   * @param date2 - The second date.
   * @returns boolean - True if the first date is later than or equals to the second date, false otherwise.
   */
  public isDateLaterThanOrEquals(date1: Date, date2: Date): boolean {
    return this.stripTime(date1) >= this.stripTime(date2);
  }

  /**
   * Checks if the first date is earlier than or equals to the second date.
   *
   * @param date1 - The first date.
   * @param date2 - The second date.
   * @returns boolean - True if the first date is earlier than or equals to the second date, false otherwise.
   */
  public isDateEarlierThanOrEquals(date1: Date, date2: Date): boolean {
    return this.stripTime(date1) <= this.stripTime(date2);
  }

  /**
   * Checks if the two dates are the same day.
   *
   * @param date1 - The first date.
   * @param date2 - The second date.
   * @returns boolean - True if the two dates are the same day, false otherwise.
   */
  public isDateSameDay(date1: Date, date2: Date): boolean {
    return this.stripTime(date1).getTime() === this.stripTime(date2).getTime();
  }

  /**
   * Generates an array of dates between the given start and end date.
   *
   * @param startDate - The start date.
   * @param endDate - The end date.
   * @returns Date[] - The array of dates between the start and end date.
   */
  public generateDaysBetweenDates(startDate: Date, endDate: Date): Date[] {
    const days: Date[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }

  /**
   * Generates a date range by days before and after the current date, optionally anchored to a crop year.
   *
   * @param daysInPast - The amount of days in the past.
   * @param daysInFuture - The amount of days in the future.
   * @param cropYear - The year to use for the current date while preserving its month and day.
   * @returns Date[] - The generated date range.
   */
  public generateDateRange(daysInPast: number, daysInFuture: number, cropYear?: number): Date[] {
    if (cropYear !== undefined) {
      const dateInCropYear = this.dateInCropYear(cropYear);

      return this.generateDaysBetweenDates(
        this.shiftDateByDays(dateInCropYear, -daysInPast),
        this.shiftDateByDays(dateInCropYear, daysInFuture),
      );
    }

    const startDate = this.daysInThePast(daysInPast);
    const endDate = this.daysInTheFuture(daysInFuture);

    return this.generateDaysBetweenDates(startDate, endDate);
  }

  private dateInCropYear(cropYear?: number): Date {
    const currentDate = this.now();
    if (cropYear === undefined) {
      return currentDate;
    }

    const lastDayOfMonth = new Date(cropYear, currentDate.getMonth() + 1, 0).getDate();
    return new Date(
      cropYear,
      currentDate.getMonth(),
      Math.min(currentDate.getDate(), lastDayOfMonth),
      currentDate.getHours(),
      currentDate.getMinutes(),
      currentDate.getSeconds(),
      currentDate.getMilliseconds(),
    );
  }

  /**
   * Moves the given date range by the given amount of days.
   *
   * @param dateRange - The date range to move.
   * @param amountOfDays - The amount of days to move the date range.
   *
   * @returns Date[] - The moved date range.
   */
  public moveDateRange(dateRange: Date[], amountOfDays: number): Date[] {
    return dateRange.map((date) => {
      const newDate = new Date(date);
      newDate.setDate(newDate.getDate() + amountOfDays);
      return newDate;
    });
  }

  public getUTCDate(date: Date): Date {
    date = new Date(date);
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes()));
  }

  private shiftDateByDays(date: Date, amountOfDays: number): Date {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + amountOfDays);
    return this.stripTime(newDate);
  }

  private stripTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}
